import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ActivityHeatmap = ({ data }) => {
  const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DAY_SHORT = {
    Sunday: 'Sun',
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
  };

  // Process data for the heatmap
  const [chartData, setChartData] = useState({
    series: [],
    options: {}
  });

  // Function to calculate most active time
  const calculateMostActiveTime = () => {
    if (!data || data.length === 0) return "No data available";
    
    // Filter out "very low" activity entries first
    const activeData = data.filter(item => item.value > 10);
    
    if (activeData.length === 0) return "No significant activity";
    
    // Find the entry with the highest activity value
    const mostActive = activeData.reduce((max, current) => 
      current.value > max.value ? current : max, 
      activeData[0]
    );
    
    // Format the day and hour for display
    const day = mostActive.day;
    const shortDay = DAY_SHORT[day] || day;
    return `${mostActive.hour} on ${shortDay}`;
  };

  // Chart options configuration
  const getChartOptions = () => ({
        chart: {
          type: 'heatmap',
          fontFamily: 'Inter, sans-serif',
          toolbar: {
            show: false
          },
          background: 'transparent'
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false
        },
        colors: ["#3D72E4"],
        title: {
          text: '',
        },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const value = series[seriesIndex][dataPointIndex];
            const day = w.config.series[seriesIndex].name;
            const hour = w.config.series[seriesIndex].data[dataPointIndex].x;
            
            // Map numeric value to activity level
            let activityLevel = "Very Low";
            if (value > 80) activityLevel = "Extreme";
            else if (value > 60) activityLevel = "Very High";
            else if (value > 40) activityLevel = "High";
            else if (value > 20) activityLevel = "Medium";
        else if (value > 10) activityLevel = "Low";
            
            return `
              <div class="apexcharts-tooltip-custom" style="padding: 8px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border: 1px solid #eee;">
                <div style="font-size: 12px; font-weight: 500; margin-bottom: 4px;">${day} at ${hour}</div>
                <div style="font-size: 12px; color: #ABABBA;">
                  Activity: <span style="font-weight: 500;">${activityLevel}</span>
                </div>
              </div>
            `;
          }
        },
        xaxis: {
          labels: {
            rotate: 0,
            style: {
              fontSize: '12px',
              fontFamily: 'Inter, sans-serif',
              colors: '#ABABBA'
            },
            formatter: (val) => {
              // Show only even hours for better readability
              const hour = parseInt(val.split(':')[0]);
              return hour % 3 === 0 ? val : '';
            }
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: 'Inter, sans-serif',
              colors: '#ABABBA'
            }
          }
        },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 0,
            colorScale: {
              ranges: [
                {
                  from: 0,
                  to: 20,
                  color: '#EBF1FF',
                  name: 'very_low',
                },
                {
                  from: 21,
                  to: 40,
                  color: '#D6E2FF',
                  name: 'low',
                },
                {
                  from: 41,
                  to: 60,
                  color: '#ADC6FF',
                  name: 'medium',
                },
                {
                  from: 61,
                  to: 80,
                  color: '#84A9FF',
                  name: 'high',
                },
                {
                  from: 81,
                  to: 100,
                  color: '#3D72E4',
                  name: 'extreme',
                },
              ]
            }
          }
        },
        stroke: {
          width: 1,
          colors: ['#fff']
        }
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      // If no data, show empty heatmap
      const emptySeriesData = DAY_ORDER.map(day => ({
        name: DAY_SHORT[day] || day,
        data: Array.from({ length: 24 }, (_, hour) => ({
          x: `${hour.toString().padStart(2, '0')}:00`,
          y: 0
        }))
      }));
      
      setChartData({
        series: emptySeriesData,
        options: getChartOptions()
      });
      return;
    }

    // Get unique days present in the data
    const availableDays = [...new Set(data.map(d => d.day))];
    console.log('Available days in heatmap data:', availableDays);
    
    // Prepare data for ApexCharts heatmap
    const seriesData = DAY_ORDER.map(day => {
      // Filter data for this day
      const dayData = data.filter(d => d.day === day);
      
      // For each day, we need data in format { x: 'hourLabel', y: activityValue }
      const formattedData = [];
      
      // Make sure we have data for all 24 hours
      for (let hour = 0; hour < 24; hour++) {
        const hourStr = hour.toString().padStart(2, '0');
        const hourData = dayData.find(d => d.hourIndex === hour);
        
        formattedData.push({
          x: `${hourStr}:00`,
          y: hourData ? hourData.value : (availableDays.includes(day) ? 0 : 0) // 0 for days with no data
        });
      }
      
      return {
        name: DAY_SHORT[day] || day,
        data: formattedData
      };
    });

    // Set chart options
    setChartData({
      series: seriesData,
      options: getChartOptions()
    });
  }, [data]);

  // Check if we have actual data (not just empty series)
  const hasData = data && data.length > 0 && data.some(item => item.value > 0);

  if (!hasData) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Activity heatmap</h3>
          <TooltipUI
            content="Shows when your team is most active by day of week and hour of day during the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No activity data available"
          description="There's no activity data for the selected time period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  return (
    <div className="bg-white-f h-[360px] p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle">
      <div className="pl-5 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Activity heatmap</h3>
          <TooltipUI
            content="Shows when your team is most active by day of week and hour of day during the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="font-medium text-[18px] text-black-0 ">
            {calculateMostActiveTime()}
          </p>
          <span className="text-[12px] text-grey-60">is peak activity time</span>
        </div>      
      </div>
      <div className="h-60">
        {chartData.series.length > 0 && (
          <ReactApexChart
            options={chartData.options}
            series={chartData.series}
            type="heatmap"
            height="100%"
          />
        )}
      </div>
      {/* Legend below heatmap (matches dashboard screenshot) */}
      <div className="pl-5 pt-2 flex items-center justify-center gap-5 text-xs text-grey-60">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-[3px] bg-[#EBF1FF] border border-grey-20" />
          <span>Very low</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-[3px] bg-[#D6E2FF] border border-grey-20" />
          <span>Low</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-[3px] bg-[#ADC6FF] border border-grey-20" />
          <span>Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-[3px] bg-[#84A9FF] border border-grey-20" />
          <span>High</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-[3px] bg-[#3D72E4] border border-grey-20" />
          <span>Very high</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap; 